import { useState, ReactElement } from 'react';
import * as React from 'react';

import { searchItems, SearchQueryBuilder } from '@esri/arcgis-rest-portal';
import { useInfiniteQuery } from '@tanstack/react-query';
import ItemCard from './ItemCard';
import isAtBottom from '../../util/layoutUtils';
import ItemSearch from './ItemSearch';
import { useAccountManager } from '../../contexts/AccountManagerContext';

interface Props {
  addLayer: (layer: LayerItem) => void;
  currentUrl: string;
}

function queryBuilder({ type, title, owner }: SearchParams) {
  let searchQuery = new SearchQueryBuilder();
  if (type) searchQuery = searchQuery.match(type).in('type');
  if (title) searchQuery = searchQuery.and().match(title).in('title');
  if (owner) searchQuery = searchQuery.and().match(owner).in('owner');
  return searchQuery;
}

function ItemBrowser({ addLayer, currentUrl }: Props): ReactElement<Props> {
  const [searchData, setSearchData] = useState<SearchParams>({ title: '' });
  const { accountManager } = useAccountManager();

  const { data, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [
      'portal-items',
      { searchData },
      accountManager?.username,
      searchData.title,
    ],
    queryFn: async ({ pageParam = 0 }: { pageParam?: number }) => {
      const q = queryBuilder({
        type: 'Feature Service',
        owner: accountManager?.username,
        title: searchData.title,
      });

      const searchResults = await searchItems({
        q,
        authentication: accountManager,
        num: 10,
        start: pageParam,
      });
      return searchResults;
    },
    getNextPageParam: (item) =>
      item.nextStart > 0 ? item.nextStart : undefined,
  });

  return (
    <div className="h-full flex flex-col">
      <ItemSearch
        onSubmitSearch={setSearchData}
        placeholder="Search for a Feature Service"
      />

      <div
        className="grid grid-cols-1 grid-rows-1 gap-x-4 overflow-y-scroll"
        onScroll={(e) => {
          if (isAtBottom(e, 50) && hasNextPage) {
            fetchNextPage()
              .then(() => {})
              .catch(() => {});
          }
        }}
      >
        {data?.pages.flatMap((itemPage) =>
          itemPage.results.map((item) => (
            <ItemCard
              key={item.id}
              currentUrl={currentUrl}
              user={accountManager}
              item={item}
              onLayerSelected={(selectedLayer) => {
                if (item.url && selectedLayer.id !== undefined) {
                  addLayer({
                    url: `${item.url}/${selectedLayer.id}`,
                    layerDefinition: selectedLayer,
                  });
                }
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default ItemBrowser;
